import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Rating,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { api } from '@/api/client'
import { ErrorBanner, formatDate, InlineSpinner } from '@/components/common'
import { FeedbackAttachments } from '@/portal/FeedbackAttachments'

interface FeedbackContext {
  orderNumber: string
  customerName: string
  eventType?: string
  eventDate: string
  alreadySubmitted: boolean
}

const DIMENSIONS = [
  { key: 'salesExperience', label: 'Sales experience' },
  { key: 'foodQuality', label: 'Food quality' },
  { key: 'taste', label: 'Taste' },
  { key: 'presentation', label: 'Presentation' },
  { key: 'service', label: 'Service' },
  { key: 'staffBehaviour', label: 'Staff behaviour' },
  { key: 'timeliness', label: 'Timeliness' },
  { key: 'cleanliness', label: 'Cleanliness' },
  { key: 'valueForMoney', label: 'Value for money' },
] as const

type DimensionKey = (typeof DIMENSIONS)[number]['key']

export function FeedbackFormPage() {
  const { token } = useParams<{ token: string }>()
  const [scores, setScores] = useState<Partial<Record<DimensionKey, number>>>({})
  const [npsScore, setNpsScore] = useState(8)
  const [comments, setComments] = useState('')
  const [name, setName] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const { data, isLoading, error } = useQuery({
    queryKey: ['public-feedback', token],
    queryFn: () => api.publicGet<FeedbackContext>(`/api/v1/public/feedback/${token}`),
    enabled: Boolean(token),
  })

  const submit = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.publicPost(`/api/v1/public/feedback/${token}`, body),
    onSuccess: () => setSubmitted(true),
  })

  if (isLoading) return <InlineSpinner />

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Alert severity="error">This feedback link is not valid or has already been used.</Alert>
      </Container>
    )
  }

  if (!data) return null

  const anyScored = Object.values(scores).some((value) => value && value > 0)

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: { xs: 3, md: 6 } }}>
      <Container maxWidth="sm">
        <Stack spacing={1} sx={{ mb: 3 }}>
          <Typography variant="overline" color="text.secondary">
            {data.orderNumber}
          </Typography>
          <Typography variant="h4">How did we do?</Typography>
          <Typography variant="body1" color="text.secondary">
            {data.customerName} · {formatDate(data.eventDate)}
          </Typography>
        </Stack>

        {(submitted || data.alreadySubmitted) && (
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 3 }}>
            Thank you for taking the time. Your feedback goes straight to the outlet manager.
          </Alert>
        )}

        {!submitted && !data.alreadySubmitted && (
          <Card>
            <CardContent>
              <ErrorBanner error={submit.error} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Rate whatever you would like to; skip anything that did not apply.
              </Typography>

              <Stack spacing={2}>
                {DIMENSIONS.map((dimension) => (
                  <Stack
                    key={dimension.key}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="body2">{dimension.label}</Typography>
                    <Rating
                      value={scores[dimension.key] ?? 0}
                      onChange={(_event, value) =>
                        setScores((current) => ({ ...current, [dimension.key]: value ?? undefined }))
                      }
                    />
                  </Stack>
                ))}
              </Stack>

              <Divider sx={{ my: 3 }} />

              <Typography variant="body2" gutterBottom>
                How likely are you to recommend us to a friend or colleague?
              </Typography>
              <Slider
                value={npsScore}
                onChange={(_event, value) => setNpsScore(value as number)}
                min={0}
                max={10}
                step={1}
                marks
                valueLabelDisplay="auto"
                sx={{ mb: 3 }}
              />

              <FeedbackAttachments token={token ?? ''} />

              <Divider sx={{ my: 3 }} />

              <Stack spacing={2}>
                <TextField
                  label="Anything you would like to tell us?"
                  value={comments}
                  onChange={(event) => setComments(event.target.value)}
                  multiline
                  minRows={3}
                  fullWidth
                />
                <TextField
                  label="Your name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  fullWidth
                />
                <Button
                  variant="contained"
                  size="large"
                  disabled={submit.isPending || !anyScored}
                  onClick={() =>
                    submit.mutate({
                      ...scores,
                      npsScore,
                      wouldRecommend: npsScore >= 7,
                      comments: comments || undefined,
                      submittedByName: name || data.customerName,
                    })
                  }
                >
                  {submit.isPending ? 'Sending…' : 'Send feedback'}
                </Button>
                {!anyScored && (
                  <Typography variant="caption" color="text.secondary" align="center">
                    Please rate at least one area.
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        )}
      </Container>
    </Box>
  )
}
